import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router";
import { Icon, type IconName } from "./Icon";

/**
 * Where the inset separators start: at the text (16), after a 20px icon (48), after a 56px thumbnail
 * (84), or a pixel value for other leading content (16 + leading width + 12, e.g. 72 for a 44px ring).
 */
export type RowInset = "text" | "icon" | "thumb" | number;
const INSETS: Record<Exclude<RowInset, number>, string> = { text: "16px", icon: "48px", thumb: "84px" };

/** Inset grouped list: optional sentence-case header and footer around a .group card of rows. */
export function ListGroup({
  header,
  footer,
  inset = "text",
  children,
  className = "",
  testId,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  inset?: RowInset;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <section className={className}>
      {header ? <h2 className="section-header">{header}</h2> : null}
      <div
        className="group"
        style={inset === "text" ? undefined : ({ "--row-inset": typeof inset === "number" ? `${inset}px` : INSETS[inset] } as CSSProperties)}
        data-testid={testId}
      >
        {children}
      </div>
      {footer ? <p className="section-footer">{footer}</p> : null}
    </section>
  );
}

type RowProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  /** 20px leading icon, monochrome (--label-2) unless the row has a tone. */
  icon?: IconName;
  /** Custom leading content (thumbnail, status ring); replaces `icon`. */
  leading?: ReactNode;
  /** Trailing secondary text (tabular numbers). */
  value?: ReactNode;
  /** Trailing control (Toggle, badge...). */
  trailing?: ReactNode;
  /** Trailing chevron; defaults to true for `to` links. */
  chevron?: boolean;
  /** Selectable rows: trailing accent check and aria-pressed. */
  selected?: boolean;
  /** "accent" for link-like actions, "danger" for destructive rows (text only, never filled). */
  tone?: "default" | "accent" | "danger";
  /** Let the title wrap instead of truncating. */
  wrap?: boolean;
  /** Router link. */
  to?: string;
  /** External link (opens a new tab unless `download`). */
  href?: string;
  download?: boolean | string;
  onClick?: () => void;
  /** Render as <label> so tapping the row toggles the Toggle/checkbox inside it. */
  label?: boolean;
  disabled?: boolean;
  className?: string;
  testId?: string;
};

/** One grouped-list row: 52px min, 16px padding, 12px icon gap, press highlight when interactive. */
export function Row({
  title,
  subtitle,
  icon,
  leading,
  value,
  trailing,
  chevron,
  selected,
  tone = "default",
  wrap,
  to,
  href,
  download,
  onClick,
  label,
  disabled,
  className = "",
  testId,
}: RowProps) {
  const toneClass = tone === "danger" ? "text-danger" : "";
  const inner = (
    <>
      {leading ?? (icon ? <Icon name={icon} size={20} className={`shrink-0 ${tone === "default" ? "text-label-2" : tone === "accent" ? "text-accent" : ""}`} /> : null)}
      <span className="min-w-0 flex-1">
        <span className={`row-title ${wrap ? "!whitespace-normal" : ""}`}>{title}</span>
        {subtitle ? <span className={`row-subtitle ${wrap ? "!whitespace-normal" : ""}`}>{subtitle}</span> : null}
      </span>
      {value != null && value !== false ? <span className="row-value">{value}</span> : null}
      {trailing}
      {selected ? <Icon name="check" size={18} weight="bold" className="shrink-0 text-accent" /> : null}
      {(chevron ?? !!to) ? <Icon name="chevron" size={16} className="row-chevron" /> : null}
      {href && !download && chevron === undefined ? <Icon name="arrow-up-right" size={16} className="row-chevron" /> : null}
    </>
  );
  const cls = `row ${toneClass} ${className}`;
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} data-testid={testId} {...(download ? { download: download === true ? "" : download } : { target: "_blank", rel: "noopener noreferrer" })}>
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={cls} aria-pressed={selected} data-testid={testId}>
        {inner}
      </button>
    );
  }
  if (label) {
    return (
      <label className={cls} data-testid={testId}>
        {inner}
      </label>
    );
  }
  return (
    <div className={cls} data-testid={testId}>
      {inner}
    </div>
  );
}
